import * as React from "react";
import { AlertTriangle, Lock } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * The four states every surface owes (MV2-841, ds/States).
 *
 *   loading       a skeleton that MIRRORS the resolved layout's heights, so
 *                 nothing jumps when data lands — never centred grey text
 *   empty         says what would fill it and offers the action that does
 *   error         what happened, retry, and the debug id to copy
 *   no permission disables with the reason and who to ask — never hides,
 *                 except on the three sanctioned surfaces with no upgrade
 *                 path (Connectivity, /ml/*, Controls for Site Owner)
 *
 * Page-level counterparts of `TableSkeleton` / `TableEmpty` / `TableError`.
 */

/** One skeleton bar — gray-100, 4px radius, the 1.2 s opacity shimmer (zero under reduced motion). Size it to the text it stands in for. */
export function Skeleton({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      aria-hidden
      className={cn("skeleton-shimmer rounded-[4px] bg-gray-100", className)}
      {...rest}
    />
  );
}

interface StateCardProps {
  icon: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  tone?: "neutral" | "danger";
}

function StateCard({
  icon,
  title,
  description,
  children,
  className,
  tone = "neutral",
}: StateCardProps) {
  return (
    <div
      role="status"
      className={cn(
        "flex min-h-[172px] flex-col items-center justify-center gap-2 rounded-[12px] border border-gray-200 bg-surface p-4 text-center",
        className,
      )}
    >
      <span
        aria-hidden
        className={
          tone === "danger" ? "text-[color:var(--danger)]" : "text-gray-400"
        }
      >
        {icon}
      </span>
      <div className="text-[14px] font-medium text-gray-900">{title}</div>
      {description && (
        <div className="max-w-[27ch] text-[11px] leading-[1.4] text-gray-500">
          {description}
        </div>
      )}
      {children}
    </div>
  );
}

export interface EmptyStateProps {
  /** gray-400 glyph, 16px. */
  icon: React.ReactNode;
  /** "No open tickets" */
  title: string;
  /** What would fill it — "Work raised from an alarm lands here." */
  description?: React.ReactNode;
  /** The one action that fills it. */
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): React.ReactElement {
  return (
    <StateCard
      icon={icon}
      title={title}
      description={description}
      className={className}
    >
      {action}
    </StateCard>
  );
}

export interface ErrorStateProps {
  title: string;
  /** "Retried 3 times over 4 seconds. The last error was a 504 from the alarms service." */
  description?: React.ReactNode;
  /** Request / correlation id — offered as a Copy button. */
  debugId?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title,
  description,
  debugId,
  onRetry,
  className,
}: ErrorStateProps): React.ReactElement {
  const copy = () => {
    try {
      void navigator.clipboard?.writeText(debugId ?? "");
    } catch {
      /* convenience only */
    }
  };
  return (
    <StateCard
      icon={<AlertTriangle size={16} />}
      title={title}
      description={description}
      tone="danger"
      className={className}
    >
      {(onRetry || debugId) && (
        <div className="flex items-center gap-[7px]">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-[26px] items-center rounded-[6px] border border-gray-300 bg-surface px-[9px] text-[12px] font-medium text-gray-800 hover:bg-gray-50"
            >
              Retry
            </button>
          )}
          {debugId && (
            <button
              type="button"
              onClick={copy}
              className="inline-flex h-[26px] items-center rounded-[6px] px-[9px] font-mono text-[10.5px] text-gray-600 hover:bg-gray-100"
            >
              Copy {debugId}
            </button>
          )}
        </div>
      )}
    </StateCard>
  );
}

/**
 * ErrorBanner — the board kit's `errorState` (MV2-854, site/SiteError): one
 * section failed, the rest of the page stands. A single row, never a page
 * of dashes: "Couldn't load {what}." · how many retries · the request id
 * to quote · Retry · Copy id.
 */
export interface ErrorBannerProps {
  /** What failed, lower-case noun phrase: "today's energy summary". */
  what: string;
  /** Request id from the API envelope, when the response carried one. */
  debugId?: string | null;
  /** Automatic retries already made (React Query's `failureCount`). */
  retries?: number;
  onRetry?: () => void;
  className?: string;
}

export function ErrorBanner({
  what,
  debugId,
  retries = 0,
  onRetry,
  className,
}: ErrorBannerProps): React.ReactElement {
  const copy = () => {
    try {
      void navigator.clipboard?.writeText(debugId ?? "");
    } catch {
      /* clipboard unavailable — the id is still on screen */
    }
  };
  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-2.5 rounded-[8px] border border-[color:color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--danger)_8%,transparent)] px-3 py-2",
        className,
      )}
    >
      <AlertTriangle
        size={14}
        aria-hidden
        className="flex-none text-[color:var(--danger)]"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-[12.5px] font-medium text-gray-900">
          Couldn&apos;t load {what}.
        </span>
        <span className="text-[11px] text-gray-600">
          {retries > 0
            ? `Retried ${retries}× automatically.`
            : "Not retried automatically."}
          {debugId && (
            <>
              {" "}
              <span className="font-mono tabular">{debugId}</span>
            </>
          )}
        </span>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-[26px] flex-none items-center rounded-[6px] border border-gray-300 bg-surface px-[9px] text-[12px] font-medium text-gray-800 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-focus"
        >
          Retry
        </button>
      )}
      {debugId && (
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-[26px] flex-none items-center rounded-[6px] px-[9px] text-[12px] text-gray-600 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-focus"
        >
          Copy id
        </button>
      )}
    </div>
  );
}

export interface NoPermissionStateProps {
  /** "Dispatch is read-only for you" */
  title: string;
  /** What it needs and who to ask — "Sending a command needs send_controls. You can see state and history." */
  description: React.ReactNode;
  /** The control the persona cannot press, rendered disabled with its reason. */
  action?: React.ReactNode;
  className?: string;
}

export function NoPermissionState({
  title,
  description,
  action,
  className,
}: NoPermissionStateProps): React.ReactElement {
  return (
    <StateCard
      icon={<Lock size={16} />}
      title={title}
      description={description}
      className={className}
    >
      {action}
    </StateCard>
  );
}

/**
 * Disabled with reason — the affordance behind "never hide". Wraps one
 * control: dashed border, 45% opacity, `aria-disabled`, the reason as tooltip
 * and (by default) as 11.5px text beside it.
 */
export function DisabledWithReason({
  reason,
  children,
  showReason = true,
  className,
}: {
  reason: string;
  children: React.ReactElement;
  showReason?: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <span className={cn("inline-flex items-center gap-3", className)}>
      <span
        aria-disabled="true"
        title={reason}
        className="inline-flex cursor-not-allowed rounded-[8px] border border-dashed border-gray-300 opacity-45 [&>*]:pointer-events-none"
      >
        {children}
      </span>
      {showReason && (
        <span className="text-[11.5px] leading-[1.4] text-gray-600">
          {reason}
        </span>
      )}
    </span>
  );
}
